import React, { useMemo, useState } from "react";
import {
  ReservationColumnKey,
  SettlementDataTable,
} from "../components/SettlementDataTable";
import MultiSelect from "@/components/common/MultiSelect";
import { Button } from "@/components/ui/button";
import {
  Download,
  HandPlatter,
  MessageSquare,
  TrendingUp,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { exportSettlementBookingsToCSV } from "@/components/pages/settlement/utils/exportSettlementBookingsToCSV";
import { useInHouse } from "@/components/pages/settlement/hooks/useSettlement";
import { useInHouseSummary } from "@/components/pages/settlement/hooks/useSettlementSummary";
import { useBookingSources } from "@/components/pages/settlement/hooks/useBookingSources";
import { useHotel } from "@/hooks/useHotel";
import { useCheckboxSelection } from "@/hooks/useCheckboxSelection";
import { useGuestNationalities } from "@/components/pages/reservations/guests/hooks/useGuests";
import { SettlementFiltersBar } from "../components/SettlementFiltersBar";
import * as countries from "i18n-iso-countries";
import { SettlementSummaryBar } from "../components/SettlementSummaryBar";
import { StatCard } from "../components/StatCard";
import { getSafeValue } from "@/components/pages/settlement/utils/getSafeValue";
import { useTranslation } from "react-i18next";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";

const inHouseColumns: ReservationColumnKey[] = [
  "select",
  "mainGuest",
  "guests",
  "room",
  "roomService",
  "arrivalDate",
  "departureDate",
  "requests",
  "balance",
  "notesCount",
  "actions",
];

const perPage = 10;

export const InHousePage = () => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | null>(
    null,
  );
  const [channels, setChannels] = useState<string[]>([]);
  const [nationality, setNationality] = useState<string[]>([]);

  const { hotelId } = useHotel();
  const { capabilities } = useAccessCapabilities();

  const { reservations, loading, totalPages } = useInHouse(
    {
      currentPage,
      perPage,
      sortField,
      sortDirection,
      channels,
      nationality,
    },
    hotelId,
  );
  const { data: summary } = useInHouseSummary(hotelId);

  const selectionOptions = useCheckboxSelection({
    data: reservations,
    getId: (r) => r.id,
  });

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection((prev) => {
        if (prev === "asc") return "desc";
        if (prev === "desc") return null;
        return "asc";
      });
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const { data: bookingSources } = useBookingSources(hotelId);
  const { data: nationalitiesData } = useGuestNationalities(hotelId);

  const nationalityOptions = (nationalitiesData || []).map((countryCode) => ({
    value: countryCode,
    label: countries.getName(countryCode, "en") || countryCode,
  }));

  const formattedUpSellAmount = useMemo(
    () =>
      summary?.upSellAmount === undefined
        ? "—"
        : summary.upSellAmount.toLocaleString("en-US", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }) + "$",
    [summary?.upSellAmount],
  );

  const hasSelection = selectionOptions?.selectedCount > 0;
  const visibleColumns = capabilities.canUseRequestsSystem
    ? inHouseColumns
    : inHouseColumns.filter(
        (column) => column !== "roomService" && column !== "requests",
      );

  const handleCSVExport = () => {
    const selectedIds = selectionOptions.selectedItems;
    const dataToExport =
      selectedIds.length > 0
        ? reservations.filter((r) => selectedIds.includes(r.id))
        : reservations;

    exportSettlementBookingsToCSV(
      dataToExport,
      visibleColumns,
      t,
      "in-house-export.csv",
    );
  };

  return (
    <div className="mx-auto flex max-w-[1344px] flex-col gap-4 p-12">
      <SettlementSummaryBar>
        {capabilities.canUseRequestsSystem ? (
          <>
            <StatCard
              icon={<TrendingUp className="text-light-stone size-6" />}
              label={t("reservation.statusCard.upsell")}
              value={getSafeValue(formattedUpSellAmount)}
            />
            <StatCard
              icon={<HandPlatter className="text-light-stone size-6" />}
              label={t("reservation.statusCard.servicesOrdered")}
              value={getSafeValue(summary?.serviceRequestsCount)}
            />
          </>
        ) : null}
      </SettlementSummaryBar>

      <div className={"flex flex-col gap-6"}>
        <div
          className={cn(
            "flex items-center justify-between",
            hasSelection && "bg-neutral-50 px-6 py-4",
          )}
        >
          {hasSelection ? (
            <span className="text-sm font-medium text-neutral-400">
              {selectionOptions.selectedCount} selected
            </span>
          ) : (
            <SettlementFiltersBar>
              <MultiSelect
                className={
                  "text-foreground min-h-8 min-w-[110px] gap-2 border border-slate-200 py-1 text-sm font-medium shadow-xs aria-expanded:border-b-0"
                }
                options={
                  bookingSources?.map((bs) => ({
                    label: bs.toLowerCase(),
                    value: bs,
                  })) ?? []
                }
                placeholder={"Chanel"}
                value={channels}
                onValueChange={setChannels}
                dropdownClassName="border border-slate-200 border-t-0"
              />
              <MultiSelect
                options={nationalityOptions}
                placeholder={"Country"}
                value={nationality}
                onValueChange={setNationality}
                className="text-foreground min-h-8 gap-2 border border-slate-200 py-1 text-sm font-medium shadow-xs aria-expanded:border-b-0"
                dropdownClassName="border border-slate-200 border-t-0"
              />
            </SettlementFiltersBar>
          )}

          <div className="flex items-center gap-x-2">
            {hasSelection && (
              <Button
                variant="icon-text"
                className="text-foreground h-8 min-h-8 gap-2 border border-slate-200 py-1 text-sm font-medium shadow-xs"
              >
                <MessageSquare className="size-4" />
                {t("reservation.buttons.sendMessage")}
              </Button>
            )}

            <Button
              variant="icon-text"
              onClick={handleCSVExport}
              className="text-foreground h-8 min-h-8 gap-2 border border-slate-200 py-1 text-sm font-medium shadow-xs"
            >
              <Download className="size-4" />
              {t("reservation.buttons.export")}
            </Button>

            {hasSelection && (
              <Button
                variant="icon-text"
                onClick={selectionOptions.clearSelection}
                className="h-8 min-h-8 gap-2 border border-slate-200 py-1 text-sm font-medium text-red-500 shadow-xs"
              >
                <X className="size-4" />
                {t("reservation.buttons.cancel")}
              </Button>
            )}
          </div>
        </div>

        <SettlementDataTable
          data={reservations}
          visibleColumns={visibleColumns}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          loading={loading}
          {...selectionOptions}
        />
      </div>
    </div>
  );
};
